import React, { useState } from 'react';
import { X, Database, Loader2 } from 'lucide-react';

interface CreateDatatableModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (data: { name: string; mongoCollectionName: string }) => Promise<void>;
}

const CreateDatatableModal: React.FC<CreateDatatableModalProps> = ({
    isOpen,
    onClose,
    onCreate
}) => {
    const [tableName, setTableName] = useState('');
    const [collectionName, setCollectionName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Auto-generate collection name from table name
    const handleNameChange = (value: string) => {
        setTableName(value);
        // Convert to snake_case and prefix with 'dt_'
        const generated = 'dt_' + value
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/(^_|_$)/g, '');
        setCollectionName(generated);
    };

    const handleCreate = async () => {
        if (!tableName.trim()) {
            setError('Table name is required');
            return;
        }
        if (!collectionName.trim()) {
            setError('Collection name is required');
            return;
        }

        setError(null);
        setIsCreating(true);
        try {
            await onCreate({
                name: tableName.trim(),
                mongoCollectionName: collectionName.trim()
            });
            // Reset form
            setTableName('');
            setCollectionName('');
            onClose();
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to create table');
        } finally {
            setIsCreating(false);
        }
    };

    const handleClose = () => {
        if (!isCreating) {
            setTableName('');
            setCollectionName('');
            setError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                                <Database size={20} className="text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-white">Create New Table</h2>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={isCreating}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white/80 hover:text-white disabled:opacity-50"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-sm text-gray-500 mb-6">
                        Create a new database table to store and manage your data. The collection name is used internally in MongoDB.
                    </p>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                            {error}
                        </div>
                    )}

                    {/* Table Name */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Table Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={tableName}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="e.g., Customers, Products, Orders"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            disabled={isCreating}
                            autoFocus
                        />
                    </div>

                    {/* Collection Name */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Collection Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={collectionName}
                            onChange={(e) => setCollectionName(e.target.value)}
                            placeholder="e.g., dt_customers"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono"
                            disabled={isCreating}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            MongoDB collection name (auto-generated from table name)
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={handleClose}
                            disabled={isCreating}
                            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreate}
                            disabled={isCreating || !tableName.trim() || !collectionName.trim()}
                            className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isCreating ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                'Create Table'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateDatatableModal;
